import React, { useState } from "react";
import Card from "components/card";
import { DEPARTMENT_COLORS } from "variables/constants";
import { Clock, MessageCircle, Send, X } from "lucide-react";
import Button from "components/button";
import { Form } from "antd";
import FormItem from "components/fields/FormItem";
import TextArea from "antd/es/input/TextArea";

const MessageCardDetails = ({
    initial,
    department,
    title,
    priority,
    status,
    date,
    time,
    fullMessage,
    response,
    reply,
}) => {
    const [showReplyBox, setShowReplyBox] = useState(false);
    const [form] = Form.useForm();

    const onSubmit = (formData) => {};

    const deptColors = DEPARTMENT_COLORS[department] || { bg: "bg-gray-100", icon: "text-gray-600" };

    return (
        <Card className="lg:col-span-2 ">
            <div className="opacity-100 transform-none">
                <div className="bg-white text-slate-900 flex flex-col gap-6 rounded-xl border-2 border-emerald-500 shadow-xl overflow-hidden">
                    <div className="grid auto-rows-min grid-rows-[auto_auto] items-start gap-1.5 [.border-b]:pb-6 bg-gradient-to-br from-emerald-50 to-green-50 border-b p-3 sm:p-5">
                        <div className="flex items-start gap-2 sm:gap-3 flex-1 min-w-0">
                            <span className={`relative items-center justify-center flex size-full shrink-0 overflow-hidden rounded-full w-10 h-10 sm:w-11 sm:h-11 border-2 border-white shadow-sm text-[11px] sm:text-[13px] font-semibold ${deptColors.bg} ${deptColors.icon}`}>
                                {initial}
                            </span>
                            <div className="flex-1 min-w-0">
                                <h4 className="text-[14px] sm:text-[16px] mb-1 line-clamp-2">
                                    {title}
                                </h4>
                                <p className="text-slate-700 text-[11px] sm:text-[12px] truncate">
                                    To: 
                                    <span className="ml-1 font-medium">{department}</span>
                                </p>
                                <div className="flex flex-wrap items-center gap-2 mt-1.5 ">
                                    <span className={`status-indicator ${priority?.toLowerCase()} text-[9px] uppercase`}>
                                        {priority}
                                    </span>
                                    <span className={`status-indicator ${status?.toLowerCase().replace(/\s+/g, "-")} text-[9px] uppercase`}>
                                        {status} {"priority"}
                                    </span>
                                    <span className="text-[9px] sm:text-[10px] text-slate-700 flex items-center gap-1 tracking-wide">
                                        <Clock className="w-3 h-3"/> {date} at {time}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="pb-6 p-3 sm:p-5">
                        <div className="mb-6">
                            <div className="flex items-center gap-2 mb-3">
                                <div className="p-1.5 rounded-full bg-blue-100">
                                    <Send className="w-3.5 h-3.5 text-blue-600" />
                                </div>
                                <h4 className="text-[13px] font-semibold">
                                    Your Message
                                </h4>
                            </div>
                            <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                                <div className="whitespace-pre-wrap text-[12px] sm:text-[13px] leading-relaxed text-slate-800">
                                    {fullMessage}
                                </div>
                            </div>
                        </div>
                        {!response ? (
                            <div className="mt-6 p-4 rounded-lg bg-orange-50 border border-orange-200">
                                <div className="flex items-start gap-2">
                                    <Clock className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                                    <div>
                                        <p className="text-[12px] font-semibold text-orange-900 mb-1"> 
                                            Awaiting Response
                                        </p>
                                        <p className="text-[11px] text-orange-700">
                                            The {department}  team will respond within 24-48 hours. You'll receive a notification when they reply.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )   :   (
                            <div>
                                <div>
                                    <div className="bg-slate-200 shrink-0 h-px w-full my-6"></div>
                                    <div className="flex items-center gap-2 mb-3">
                                        <div className="p-1.5 rounded-full bg-green-100">
                                            <MessageCircle className="w-3.3 h-3.5 text-green-600" />
                                        </div>
                                        <div className="flex-1">
                                            <h4 className="text-[13px] font-semibold">
                                                Department Response
                                            </h4>
                                            <p className="text-[10px] text-slate-700">
                                                {response?.date || "No response yet"} 
                                                {response?.time ? ` at ${response?.time}` : ""}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-4 border-2 border-green-200">
                                        <div className="whitespace-pre-wrap text-[12px] sm:text-[13px] leading-relaxed text-slate-800">
                                            {response?.message}
                                        </div>
                                    </div>
                                </div>
                                {reply && (
                                    <div>
                                        <div className="bg-slate-200 shrink-0 h-px w-full my-6"></div>
                                        <div className="space-y-4">
                                            <div className="flex items-center gap-2 mb-3">
                                                <div className="p-1.5 rounded-full bg-purple-100">
                                                    <MessageCircle className="w-3.5 h-3.5 text-purple-600" />
                                                </div>
                                                <h4 className="text-[13px] font-semibold">
                                                    Your Replies
                                                </h4>
                                            </div>
                                            <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-lg p-4 border-2 border-purple-200 ">
                                                <div className="whitespace-pre-wrap text-[12px] sm:text-[13px] leading-relaxed text-slate-800 mb-2">
                                                    {reply?.message}
                                                </div>
                                                <p className="text-[10px] text-slate-700 ">
                                                    Sent on {reply?.date} at {reply?.time}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <div>
                                    <div className="bg-slate-200 shrink-0 h-px w-full my-6"></div>
                                    {!showReplyBox && (
                                        <div className="flex justify-center">
                                            <Button
                                                onClick={() => setShowReplyBox(true)}
                                                className="inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-medium transition-all shrink-0 text-white hover:bg-emerald-500/90 h-9 px-4 py-2 bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 hover:shadow-none gap-2"
                                            >
                                                <MessageCircle className="w-4 h-4" />
                                                Reply to {department}
                                            </Button>
                                        </div>
                                    )}
                                    {showReplyBox && (
                                        <div className="space-y-3">
                                            <div className="flex items-center justify-between">
                                                <h4 className="text-[13px] font-semibold">
                                                    Compose Reply
                                                </h4>
                                                <Button
                                                    onClick={() => setShowReplyBox(false)}
                                                    className="inline-flex items-center justify-center whitespace-nowrap transition-all shrink-0 outline-none hover:bg-slate-100 hover:text-slate-900 h-8 hover:shadow-none rounded-md gap-1.5 px-2.5"
                                                >
                                                    <X className="w-4 h-4" />
                                                </Button>
                                            </div>
                                            <Form
                                                form={form}
                                                layout="vertical" 
                                                onFinish={onSubmit}
                                            >
                                                <FormItem>
                                                    <TextArea
                                                        className="input-field"
                                                        maxLength={255}
                                                        placeholder="Type your reply here..."
                                                        rows={2}
                                                    />
                                                </FormItem>
                                            </Form>
                                            <div className="flex gap-2 justify-end">
                                                <Button
                                                    onClick={() => setShowReplyBox(false)}
                                                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-medium
                                                                transition-all shrink-0 bg-white text-slate-900 
                                                                hover:bg-slate-100 hover:shadow-none hover:text-slate-900  h-9 px-4 py-2 border-2"
                                                >
                                                    Cancel
                                                </Button>
                                                <Button
                                                    onClick={onSubmit}
                                                    className="inline-flex items-center justify-center gap-2 rounded-xl
                                                                text-sm font-medium transition-all outline-none hover:bg-emerald-500/90 h-9 px-4 py-2 
                                                                bg-gradient-to-r from-emerald-600 to-green-600 hover:shadow-none
                                                                hover:from-emerald-700 hover:to-green-700 text-white"
                                                >
                                                    <Send className="w-4 h-4 mr-2"/>
                                                    Send Message
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Card>
    )
};
export default MessageCardDetails;